import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { membershipApplicationAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { MembershipApplication, ApplicationStatus } from '../../types';

const MembershipApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<MembershipApplication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter and search states
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus>(ApplicationStatus.PENDING);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof MembershipApplication>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch applications
  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await membershipApplicationAPI.getApplications({
        status: statusFilter,
        search: searchTerm
      });
      setApplications(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  // Fetch applications on mount and when filters change
  useEffect(() => {
    fetchApplications();
  }, [statusFilter, searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Handle view application details
  const handleViewApplication = (id: string) => {
    navigate(`/membership-applications/${id}`);
  };

  // Format currency
  const formatCurrency = (amount: string | number | undefined) => {
    if (amount === undefined) return '$0.00';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
  };

  // Get status chip color
  const getStatusColor = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return 'warning';
      case ApplicationStatus.APPROVED:
        return 'success';
      case ApplicationStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  // Handle sort
  const handleRequestSort = (property: keyof MembershipApplication) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter and sort applications
  const filteredAndSortedApplications = React.useMemo(() => {
    // First filter by search term
    let filtered = applications;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = applications.filter(application => 
        application.firstName?.toLowerCase().includes(search) ||
        application.lastName?.toLowerCase().includes(search) ||
        application.email?.toLowerCase().includes(search) ||
        `${application.firstName} ${application.lastName}`.toLowerCase().includes(search)
      );
    }

    // Then sort the filtered results
    return filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return order === 'asc' ? 1 : -1;
      if (bValue === undefined) return order === 'asc' ? -1 : 1;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [applications, order, orderBy, searchTerm]);

  // Paginate applications
  const paginatedApplications = filteredAndSortedApplications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Container>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>

          <Typography color="text.primary">Membership Applications</Typography>
        </Breadcrumbs>

        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
          Membership Applications
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus)}
            >
              <MenuItem value={ApplicationStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={ApplicationStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={ApplicationStatus.REJECTED}>Rejected</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder="Search by name or email..."
          />
        </Box>
      </Box>

      {/* Data Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'firstName'}
                    direction={orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('firstName')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'membershipTierName'}
                    direction={orderBy === 'membershipTierName' ? order : 'asc'}
                    onClick={() => handleRequestSort('membershipTierName')}
                  >
                    Membership Tier
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'monthlyPrice'}
                    direction={orderBy === 'monthlyPrice' ? order : 'asc'}
                    onClick={() => handleRequestSort('monthlyPrice')}
                  >
                    Monthly Fee
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Submitted
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell>Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedApplications.map((application) => (
                  <TableRow
                    hover
                    key={application.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewApplication(application.id)}
                  >
                    <TableCell>
                      {`${application.firstName} ${application.lastName}`}
                    </TableCell>
                    <TableCell>{application.email}</TableCell>
                    <TableCell>{application.membershipTierName}</TableCell>
                    <TableCell>{formatCurrency(application.monthlyPrice)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={application.status} 
                        color={getStatusColor(application.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(application.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </TableCell>
                    {/* <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewApplication(application.id);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredAndSortedApplications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default MembershipApplicationsPage;
